import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { LogIn, ShieldCheck } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { LOGO_URL } from "../../components/brand";

export default function AdminLogin() {
  const { user, login, verifyMfa, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // When the password leg succeeds but the account has 2FA, the server
  // returns a short-lived challenge_token. We pivot the form to a
  // single-input "enter your code" step instead of redirecting.
  const [mfaStep, setMfaStep] = useState(null); // { challenge_token, email }
  const [code, setCode] = useState("");

  if (loading) return <div className="min-h-screen admin-bg flex items-center justify-center font-display text-[#5D2C8F]">Loading…</div>;
  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const result = await login(email, password);
      if (result?.mfa_required) {
        setMfaStep({ challenge_token: result.challenge_token, email });
        toast.success("Enter the 6-digit code from your authenticator app.");
      } else {
        toast.success("Welcome back.");
        navigate("/admin");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verifyMfa(mfaStep.challenge_token, code.trim());
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Code didn't match — try the next one");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen admin-bg flex items-center justify-center p-6" data-testid="admin-login-page">
      <Toaster position="top-center" />
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="Seasons Art Class — East Croydon" className="h-24 w-auto object-contain mx-auto" />
          <div className="font-display text-[#5D2C8F] text-xl mt-3">admin &amp; CRM</div>
        </div>
        {!mfaStep ? (
          <form onSubmit={onSubmit} className="admin-surface p-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label className="text-sm text-[#5D2C8F] font-medium">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#E8C37A]"
                data-testid="admin-login-email" />
            </div>
            <div>
              <label className="text-sm text-[#5D2C8F] font-medium">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#E8C37A]"
                data-testid="admin-login-password" />
            </div>
            <button type="submit" disabled={busy} className="sac-btn-primary w-full justify-center" data-testid="admin-login-submit-btn">
              {busy ? "Signing in…" : <>Sign in <LogIn size={16} /></>}
            </button>
          </form>
        ) : (
          <form onSubmit={onVerify} className="admin-surface p-8 space-y-5" data-testid="admin-login-mfa-form">
            <div className="text-center">
              <ShieldCheck size={32} className="mx-auto text-[#5D2C8F]" />
              <div className="font-display text-lg text-[#5D2C8F] mt-2">Two-factor authentication</div>
              <p className="text-xs text-[#6E6074] mt-1">Open Google Authenticator (or Authy) and enter the 6-digit code for <strong>{mfaStep.email}</strong>.</p>
            </div>
            <div>
              <label className="text-sm text-[#5D2C8F] font-medium">6-digit code</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-4 py-2.5 text-center text-2xl tracking-[0.4em] focus:outline-none focus:ring-2 focus:ring-[#E8C37A]"
                data-testid="admin-login-mfa-code"
                placeholder="123 456"
              />
            </div>
            <button type="submit" disabled={busy || code.length !== 6} className="sac-btn-primary w-full justify-center disabled:opacity-50" data-testid="admin-login-mfa-submit">
              {busy ? "Verifying…" : "Sign in"}
            </button>
            <button type="button" onClick={() => { setMfaStep(null); setCode(""); }} className="w-full text-xs text-[#A685BD] hover:underline" data-testid="admin-login-mfa-back">← Back to login</button>
          </form>
        )}
      </div>
    </div>
  );
}
